/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { previewEvents } from "./previewEvents";
import { eventNames, privateEventNames, constants } from "publicApis/constants";
import { getPageBeforeOrAtOffset, getCurrentPageNumber } from "selectors/pageNumber";
import { getPageHeight } from "selectors/pageSelectors";
import { getViewPortHeight, getDocumentVisibleHeight } from "selectors/documentViewSelectors";
import { PAGE_BOTTOM_MARGIN, CERTIFICATE_KEYS } from "constants/constants";
import { isEqual } from "utils/lodashUtils";
import {
  getPageHandle,
  isSinglePageMode,
  shouldTriggerDocumentViewUpdateEvent,
  isFullScreenOrFitOnePageMode,
  getViewRotationAngle
} from "selectors/documentSelectors";
import { hasScrollbar, getPagesViewModeData } from "utils/PageUtils";
import {
  getPagesViewWidth,
  getPagesViewXPos,
  getPagesViewYPos,
  getPagesViewTotalHeight,
  getPagesViewElementHandle
} from "selectors/pagedViewState";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { getScrollbarSize } from "selectors/appUiStateSelectors";
import { getPageZoomLevel } from "selectors/zoom";

let previousPagesInView = null;
let previousDocumentStateInfo = null;
let previousDocumentViewInfo = null;

export const resetPreviewEvents = () => {
  previewEvents.reset();
  previousPagesInView = null;
  previousDocumentViewInfo = null;
  previousDocumentStateInfo = null;
};

export const triggerHyperlinkClickEvent = (params, url) => {
  let payload = {
    type: url ? constants.HYPERLINK_TYPE_EXTERNAL : constants.HYPERLINK_TYPE_INTERNAL,
    pageNumber: params ? params.pageNumber : undefined,
    x: params ? params.xCoordinate : undefined,
    y: params ? params.yCoordinate : undefined
  };

  if (url) {
    payload.url = url;
  }

  previewEvents.trigger(eventNames.HYPERLINK_CLICK, payload);
};

export const triggerPageMouseLeaveEvent = (evt, index) => {
  previewEvents.trigger(eventNames.PAGE_MOUSE_LEAVE, {
    eventObj: evt,
    params: { pageIndex: index }
  });
};

export const triggerPageMouseEnterEvent = (evt, index) => {
  previewEvents.trigger(eventNames.PAGE_MOUSE_ENTER, {
    eventObj: evt,
    params: { pageIndex: index }
  });
};

export const triggerSearchEvent = text => {
  previewEvents.trigger(eventNames.TEXT_SEARCH, {
    searchedText: text
  });
};

export const triggerSearchClearEvent = () => {
  previewEvents.trigger(eventNames.SEARCH_CLEAR);
};

export const triggerCurrentPageChangeEvent = pageNum => {
  previewEvents.trigger(eventNames.CURRENT_PAGE_CHANGE, {
    pageNumber: pageNum
  });
};

export const triggerTextCopyEvent = text => {
  previewEvents.trigger(eventNames.TEXT_COPY, {
    copiedText: text
  });
};

export const triggerBookmarkClickEvent = item => {
  previewEvents.trigger(eventNames.BOOKMARK_ITEM_CLICK, {
    title: item
  });
};

export const triggerDownloadEvent = () => {
  previewEvents.trigger(eventNames.DOCUMENT_DOWNLOAD);
};

export const triggerDocumentOpenedEvent = () => {
  previewEvents.trigger(eventNames.DOCUMENT_OPEN);
};

export const triggerUnsupportedFeatureFoundEvent = (featureName, data) => {
  let payload = {
    featureName
  };
  if (data) {
    payload.data = data;
  }
  previewEvents.trigger(eventNames.UNSUPPORTED_FEATURE_FOUND, payload);
};

export const triggerContextMenuItemClicked = payload => {
  previewEvents.trigger(eventNames.CONTEXT_MENU_ITEM_CLICKED, payload);
};

const shouldTriggerPagesInViewEvent = payload => {
  if (previousPagesInView === null) {
    return true;
  }
  return !isEqual(previousPagesInView, payload);
};

const getBoundingClientRect = element => {
  const { top, right, bottom, left, width, height, x, y } = element.getBoundingClientRect();
  return {
    top,
    right,
    bottom,
    left,
    width,
    height,
    x,
    y
  };
};

const getPageAreaRects = (stores, startIndex, endIndex) => {
  const pagesViewHandle = getPagesViewElementHandle(stores);
  if (!pagesViewHandle) return;
  if (!pagesViewHandle.getBoundingClientRect) return;
  const pagesViewRect = getBoundingClientRect(pagesViewHandle);
  const horizontalScrollbarWidth = hasScrollbar(pagesViewHandle, "horizontal") ? getScrollbarSize(stores) : 0;
  const verticalScrollbarWidth = hasScrollbar(pagesViewHandle, "vertical") ? getScrollbarSize(stores) : 0;

  const pageAreaRects = [];

  for (let index = startIndex; index <= endIndex; index++) {
    const page = getPageHandle(stores, index);
    if (page) {
      const pageRect = getBoundingClientRect(page);
      const modifiedRect = {
        left: Math.max(pageRect.left, pagesViewRect.left),
        right: Math.min(pageRect.right, pagesViewRect.right - verticalScrollbarWidth),
        bottom: Math.min(pageRect.bottom, pagesViewRect.bottom - horizontalScrollbarWidth),
        top: Math.max(pageRect.top, pagesViewRect.top)
      };
      pageAreaRects.push(modifiedRect);
    }
  }
  return pageAreaRects;
};

export const calculatePagesInViewData = stores => {
  let payload;
  if (isSinglePageMode(stores) || isFullScreenOrFitOnePageMode(stores)) {
    const currPageNumber = getCurrentPageNumber(stores);
    payload = {
      startPage: {
        pageNumber: currPageNumber,
        fractionVisible: 1
      },
      endPage: {
        pageNumber: currPageNumber,
        fractionVisible: 1
      }
    };
  } else {
    let startPage = getPageBeforeOrAtOffset(stores, 0);

    if (startPage.pageBottomMargin !== -1) {
      startPage = getPageBeforeOrAtOffset(stores, PAGE_BOTTOM_MARGIN - startPage.pageBottomMargin);
    }

    let startPageHeight = getPageHeight(stores, startPage.pageIndex);
    // Bug fix: for in place mode the viewportHeight corresponds to height of one page * total pages hence end page always turns out to be the total no of pages in PDF. Hence, we need to get the iframe height.
    let docViewHeight = getIsInPlaceMode(stores) ? getDocumentVisibleHeight(stores) : getViewPortHeight(stores);
    let offsetForEndPage = docViewHeight;

    let endPage = getPageBeforeOrAtOffset(stores, offsetForEndPage);

    if (endPage.pageBottomMargin !== -1) {
      offsetForEndPage = docViewHeight - endPage.pageBottomMargin;
      endPage = getPageBeforeOrAtOffset(stores, offsetForEndPage);
    }

    let endPageHeight = getPageHeight(stores, endPage.pageIndex);

    let startPageVisibleHeight =
      endPage.pageIndex === startPage.pageIndex
        ? endPage.yInPage - startPage.yInPage
        : startPageHeight - startPage.yInPage;
    let endPageVisibleHeight =
      endPage.pageIndex === startPage.pageIndex ? endPage.yInPage - startPage.yInPage : endPage.yInPage;

    payload = {
      startPage: {
        pageNumber: startPage.pageIndex + 1,
        fractionVisible: startPageVisibleHeight / startPageHeight
      },
      endPage: {
        pageNumber: endPage.pageIndex + 1,
        fractionVisible: endPageVisibleHeight / endPageHeight
      }
    };
    payload.startPage.fractionVisible = parseFloat(
      Math.min(1, Math.max(0, payload.startPage.fractionVisible)).toFixed(2)
    );
    payload.endPage.fractionVisible = parseFloat(Math.min(1, Math.max(0, payload.endPage.fractionVisible)).toFixed(2));
  }
  return payload;
};

export const updatePageAreaRects = (stores, startIndex, endIndex) => {
  if (startIndex === undefined || endIndex === undefined) {
    // calculate the indices if not passed as params
    let payload = calculatePagesInViewData(stores);
    startIndex = payload.startPage.pageNumber - 1;
    endIndex = payload.endPage.pageNumber - 1;
  }
  stores.pagedViewState.setPageAreaRects(getPageAreaRects(stores, startIndex, endIndex));
};

export const triggerPagesInViewEvent = stores => {
  let payload = calculatePagesInViewData(stores);
  updatePageAreaRects(stores, payload.startPage.pageNumber - 1, payload.endPage.pageNumber - 1);

  if (shouldTriggerPagesInViewEvent(payload)) {
    previousPagesInView = payload;
    previewEvents.trigger(eventNames.PAGES_IN_VIEW_CHANGE, payload);
  }
};

export const triggerCustomizeTopBarEvent = ({ topbarConfig, sideMenuConfig, layoutConfig }) => {
  previewEvents.trigger(privateEventNames.CUSTOMIZE_TOP_BAR, {
    topbarConfig,
    sideMenuConfig,
    layoutConfig
  });
};

export const triggerDialogOpenEvent = () => {
  previewEvents.trigger(eventNames.MODAL_DIALOG_OPEN);
};

export const triggerDialogCloseEvent = () => {
  previewEvents.trigger(eventNames.MODAL_DIALOG_CLOSE);
};

export const triggerPrintActionEvent = printBrowserSupportStatus => {
  previewEvents.trigger(eventNames.DOCUMENT_PRINT, {
    printSupported: printBrowserSupportStatus
  });
};

export const triggerCompletePDFBufferAvailableEvent = () => {
  previewEvents.trigger(eventNames.COMPLETE_PDF_BUFFER_AVAILABLE);
};

export const triggerFallbackPrintEvent = (statusInfo, timeout) => {
  previewEvents.trigger(eventNames.FALLBACK_PRINT, {
    statusInfo,
    timeout
  });
};

export const triggerPreviewErrorEvent = errorInfo => {
  previewEvents.trigger(eventNames.ERROR_OCCURRED, {
    errorInfo,
    clientName: "preview"
  });
};

export const triggerSubmitFormEvent = (serverURL, data, responseHandler) => {
  previewEvents.trigger(eventNames.SUBMIT_FORM, {
    resource: serverURL + (data.method === "GET" ? `?${data.formData}` : ""),
    options: {
      method: data.method,
      headers: {
        "Content-Type": data.method === "POST" ? data.contentType : undefined
      },
      body: data.method === "POST" ? data.formData : undefined
    },
    triggeredFrom: data.triggeredFrom,
    responseHandler
  });
};

export const triggerOpenDocumentInDesktopEvent = () => {
  previewEvents.trigger(eventNames.OPEN_DOCUMENT_IN_DESKTOP, {});
};

export const triggerDigSigValidationStatusUpdatedEvent = signatureData => {
  const { pdfValidationSummary, signatures } = signatureData;

  const getSignerCertificateData = certificates => {
    if (!certificates.length) {
      return {};
    }

    return {
      "Serial Number": certificates[0][CERTIFICATE_KEYS.serialNumber],
      Subject: certificates[0][CERTIFICATE_KEYS.subject] || {},
      Issuer: certificates[0][CERTIFICATE_KEYS.issuer] || {}
    };
  };

  const newSignaturesData = signatures.map(signature => ({
    type: signature.signatureDetails.type,
    subfilter: signature.digestValidation.subfilter,
    signerCertificate: getSignerCertificateData(signature.certificates)
  }));

  const payload = {
    status: pdfValidationSummary.status,
    message: pdfValidationSummary.message,
    validationIdentifiers: pdfValidationSummary.validationIdentifiers,
    signatures: newSignaturesData,
    unsupportedSignatures: pdfValidationSummary.unsupportedSignatures
  };

  previewEvents.trigger(eventNames.DIGSIG_VALIDATION_STATUS_UPDATED, payload);
};

export const triggerWaitingOnCompletePDFBufferEvent = () => {
  previewEvents.trigger(eventNames.WAITING_ON_COMPLETE_PDF_BUFFER);
};

export const triggerDocumentViewThemeChange = payload => {
  previewEvents.trigger(eventNames.DOCUMENT_VIEW_THEME_CHANGE, payload);
};

export const triggerDocumentViewStateEvent = stores => {
  const x = getPagesViewXPos(stores);
  const { yInPage, pageIndex } = getPageBeforeOrAtOffset(stores);
  const zoomLevel = getPageZoomLevel(stores, pageIndex);
  const rotationAngle = getViewRotationAngle(stores);
  const pageNumber = pageIndex + 1;

  const viewModeData = getPagesViewModeData(stores);

  const payload = {
    x: x / zoomLevel,
    yInPage: yInPage / zoomLevel,
    rotationAngle,
    pageNumber,
    zoomLevel: zoomLevel * 100,
    viewModeData,
    scrollWidth: getPagesViewWidth(stores),
    scrollHeight: getPagesViewTotalHeight(stores),
    pagesViewYPos: getPagesViewYPos(stores)
  };
  if (!isEqual(previousDocumentStateInfo, payload)) {
    previewEvents.trigger(eventNames.DOCUMENT_VIEW_STATE_UPDATE, payload);
  }
  previousDocumentStateInfo = payload;
};

export const triggerDocumentViewUpdateEvent = stores => {
  const payload = {
    scrollWidth: getPagesViewWidth(stores),
    scrollHeight: getPagesViewTotalHeight(stores),
    x: getPagesViewXPos(stores),
    y: getPagesViewYPos(stores)
  };

  if (shouldTriggerDocumentViewUpdateEvent(stores, previousDocumentViewInfo, payload)) {
    previewEvents.trigger(eventNames.DOCUMENT_VIEW_UPDATE, payload);
  } else {
    stores.documentStore.setTriggerDocumentViewUpdateEvent(true);
  }
  previousDocumentViewInfo = payload;
};
